import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getDashboardRoute } from '../auth/getDashboardRoute';
import { confirmAbandonReportGeneration } from '../utils/reportGenerationGuard';

interface AuthNavMenuProps {
  className?: string;
  mobile?: boolean;
  onNavigate?: () => void;
}

export function AuthNavMenu({
  className = '',
  mobile = false,
  onNavigate,
}: AuthNavMenuProps) {
  const basename = process.env.PUBLIC_URL || '';
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAndNavigate = async (path: string) => {
    const shouldLeave = await confirmAbandonReportGeneration('navigate away');
    if (!shouldLeave) {
      setOpen(false);
      return;
    }
    setOpen(false);
    onNavigate?.();
    navigate(path);
  };

  const handleLogout = async () => {
    const shouldLeave = await confirmAbandonReportGeneration('log out');
    if (!shouldLeave) {
      setOpen(false);
      return;
    }
    setOpen(false);
    onNavigate?.();
    try {
      await logout();
    } finally {
      navigate('/signin', { replace: true });
    }
  };

  if (isLoading) {
    return null;
  }

  const displayName =
    user?.userName?.trim() ||
    user?.email?.split('@')[0] ||
    'Account';

  if (!isAuthenticated || !user) {
    if (mobile) {
      return (
        <li>
          <Link to="/signin" className="mobile-signin" onClick={onNavigate}>
            Sign In
          </Link>
        </li>
      );
    }

    return (
      <Link
        to="/signin"
        className={`hero__cta_2 hero__btn-primary signin-button auth-nav-menu__sign-in ${className}`}
      >
        Sign In
      </Link>
    );
  }

  const dashboardPath = getDashboardRoute(user, basename);

  if (mobile) {
    return (
      <>
        <li className="mobile-nav-menu__auth-start">
          <button
            type="button"
            className="mobile-nav-auth-btn"
            onClick={() => closeAndNavigate(dashboardPath)}
          >
            Dashboard
          </button>
        </li>
        <li>
          <button
            type="button"
            className="mobile-nav-auth-btn"
            onClick={handleLogout}
          >
            Logout
          </button>
        </li>
      </>
    );
  }

  return (
    <div className={`auth-nav-menu ${className}`} ref={menuRef}>
      <button
        type="button"
        className="auth-nav-menu__trigger hero__cta_2 signin-button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="auth-nav-menu__name">{displayName}</span>
        <span className="auth-nav-menu__chevron" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="auth-nav-menu__dropdown" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => closeAndNavigate(dashboardPath)}
          >
            Dashboard
          </button>
          <button type="button" role="menuitem" onClick={handleLogout}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default AuthNavMenu;
